import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { LocalStorage } from '../../local-storage.class';
import { Feature } from '../../models/auth/feature.interface';
import { Functionality } from '../../models/auth/functionality.interface';
import { Role } from '../../models/auth/role.interface';
import { User } from '../../user.interface';
import { ApiService } from '../api/api.service';
import { ConditionalFunctions } from './conditional-functions';
import { Features } from './features';
import { Functionalities } from './functionalities';

@Injectable({
    providedIn: 'root',
})
export class AuthorisationService {
    static get roleFunctionalityMap(): Map<string, Functionalities[]> {
        return AuthorisationService._roleFunctionalityMap;
    }

    static get functionalityFeaturesMap(): Map<Functionalities, Features[]> {
        return AuthorisationService._functionalityFeaturesMap;
    }

    private static _roleFunctionalityMap: Map<string, Functionalities[]>;
    private static _functionalityFeaturesMap: Map<Functionalities, Features[]>;

    constructor(public apiService: ApiService) {}

    rightsMap() {
        return this.apiService
            .getCallData$<{
                rolesHasFunctionalities: { functionality_key: Functionalities; role_key: string }[];
                functionalitiesHasFeatures: { functionality_key: Functionalities; feature_key: Features }[];
            }>('auth/rights-map')
            .pipe(
                shareReplay(1),
                map(
                    (rights: {
                        rolesHasFunctionalities: { functionality_key: Functionalities; role_key: string }[];
                        functionalitiesHasFeatures: { functionality_key: Functionalities; feature_key: Features }[];
                    }) => {
                        const roleFunctionalityMap = new Map<string, Functionalities[]>();
                        rights.rolesHasFunctionalities.forEach(({ role_key, functionality_key }) => {
                            if (!roleFunctionalityMap.has(role_key)) {
                                roleFunctionalityMap.set(role_key, []);
                            }
                            roleFunctionalityMap.get(role_key).push(functionality_key);
                        });
                        const functionalityFeaturesMap = new Map<Functionalities, Features[]>();
                        rights.functionalitiesHasFeatures.forEach(({ functionality_key, feature_key }) => {
                            if (!functionalityFeaturesMap.has(functionality_key)) {
                                functionalityFeaturesMap.set(functionality_key, []);
                            }
                            functionalityFeaturesMap.get(functionality_key).push(feature_key);
                        });
                        AuthorisationService._roleFunctionalityMap = roleFunctionalityMap;
                        AuthorisationService._functionalityFeaturesMap = functionalityFeaturesMap;

                        return { roleFunctionalityMap, functionalityFeaturesMap };
                    }
                )
            );
    }

    public static hasRole(roleKey: string, otherUser?: User, params?: any): boolean {
        const user = otherUser ?? LocalStorage.getUser();
        return !!user?.roles?.find(
            role =>
                role.key === roleKey &&
                (!role.conditional_function || ConditionalFunctions[role.conditional_function](user, role, params))
        );
    }

    public static hasAnyRole(roleKeys: string[], otherUser?: User): boolean {
        return !!roleKeys.find(roleKey => {
            return this.hasRole(roleKey, otherUser);
        });
    }

    public static hasFunctionality(functionalityKey: Functionalities, otherUser?: User): boolean {
        const user = otherUser ?? LocalStorage.getUser();
        return !!user?.roles?.find(role => {
            return this.roleFunctionalityMap.get(role.key)?.find(functionality => functionality === functionalityKey);
        });
    }

    public static hasAnyFunctionality(
        keys: Functionalities | Functionalities[],
        user: User = LocalStorage.getUser()
    ): boolean {
        const keyList = [keys].flat();
        return keyList.some(key => this.hasFunctionality(key, user));
    }

    public static hasFeature(featureKey: Features, otherUser?: User): boolean {
        const user = otherUser ?? LocalStorage.getUser();
        return !!user?.roles?.find(role => {
            return this.roleFunctionalityMap
                .get(role.key)
                ?.find(functionality =>
                    this.functionalityFeaturesMap.get(functionality)?.find(feature => feature === featureKey)
                );
        });
    }

    public static hasAnyFeature(featureKeys: Features[], otherUser?: User): boolean {
        const user = otherUser ?? LocalStorage.getUser();
        return !!featureKeys.find(featureKey => {
            return this.hasFeature(featureKey, user);
        });
    }

    public getRolesMap(): Observable<Map<string, Role>> {
        return this.apiService.getCallData$<Role[]>('auth/roles').pipe(
            shareReplay(1),
            map(roles => {
                const roleMap = new Map<string, Role>();
                roles.forEach(role => roleMap.set(role.key, role));
                return roleMap;
            })
        );
    }

    public getRoles(): Observable<Role[]> {
        return this.apiService.getCallData$<Role[]>('auth/roles');
    }

    public getFunctionalities(): Observable<Functionality[]> {
        return this.apiService.getCallData$<Functionality[]>('auth/functionalities');
    }

    public getFeatures(): Observable<Feature[]> {
        return this.apiService.getCallData$<Feature[]>('auth/features');
    }
}
